import { Check, ChevronDown } from 'lucide-react';
import { Select as RadixSelect } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Input } from './input';
import { menuItem, menuSurface } from './menu-styles';
import { useReadOnly } from './read-only-context';

export interface SelectOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Plain text for the trigger when `label` is rich. */
  text?: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string> {
  value: T;
  options: readonly SelectOption<T>[];
  onValueChange: (value: T) => void;
  id?: string;
  /** Accessible name when there is no visible <label htmlFor>. */
  'aria-label'?: string;
  className?: string;
  disabled?: boolean;
}

export function Select<T extends string>({
  value,
  options,
  onValueChange,
  id,
  className,
  disabled,
  ...aria
}: SelectProps<T>) {
  const readOnly = useReadOnly();
  if (readOnly) {
    const selected = options.find((o) => o.value === value);
    const text = selected?.text ?? (typeof selected?.label === 'string' ? selected.label : value);
    return (
      <Input
        readOnly
        value={text}
        {...(id === undefined ? {} : { id })}
        aria-label={aria['aria-label']}
        className={cn('truncate', className)}
      />
    );
  }
  return (
    <RadixSelect.Root
      value={value}
      onValueChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onValueChange(option.value);
      }}
      {...(disabled === undefined ? {} : { disabled })}
    >
      <RadixSelect.Trigger
        id={id}
        aria-label={aria['aria-label']}
        className={cn(
          'inline-flex h-7 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border-strong bg-bg-2 px-2 text-left text-sm text-text-1',
          'outline-none hover:border-text-4 focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-subtle',
          'disabled:opacity-50 data-[placeholder]:text-text-4',
          className,
        )}
      >
        <span className="truncate">
          <RadixSelect.Value />
        </span>
        <RadixSelect.Icon>
          <ChevronDown className="size-3.5 text-text-3" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            menuSurface,
            'max-h-[min(var(--radix-select-content-available-height),320px)] min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                {...(option.text === undefined ? {} : { textValue: option.text })}
                {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
                className={cn(menuItem, 'pr-7')}
              >
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="absolute right-2 inline-flex">
                  <Check className="!text-text-1" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
